import React from "react";
import { Grid, Box, Typography, Container } from "@mui/material";
import { motion } from "framer-motion";
import Header from "../../layout/Header";

/**
 * HeroSection is a reusable child component for hero/first-section layouts.
 * The Header is always rendered at the top of the hero section.
 *
 * If `bannerImage` is provided, it will be used as a background image,
 * with a linear gradient overlay on top of the banner image.
 * The left side shows heading/description, the right side shows image (if provided).
 * Animations are applied to both text and image.
 *
 * The `image` prop is optional if `bannerImage` is provided.
 *
 * If no heading, description, image, bannerImage, or gradientBg is provided, render nothing.
 *
 * The banner/gradient background always has height 100vh.
 */
const HeroSection = ({
  heading,
  description,
  image,
  imageAlt = "Hero Image",
  leftContentSx = {},
  rightImageSx = {},
  containerSx = {},
  headingSx = {},
  descriptionSx = {},
  reverse = false, // if true, image on left, text on right
  bannerImage,
  gradientBg,
  gradientOpacity = 1,
}) => {
  // If no data is provided, render nothing
  const hasLeftContent = !!heading || !!description;
  const hasRightContent = !!image;
  const hasBackground = !!bannerImage || !!gradientBg;

  if (!hasLeftContent && !hasRightContent && !hasBackground) {
    return null;
  }

  // Animation variants for left and right content
  const leftVariant = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const headingVariant = {
    hidden: { opacity: 0, y: -32 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: 0.2, ease: "easeOut" },
    },
  };

  const textVariant = {
    hidden: { opacity: 0, y: 32 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: 0.5, ease: "easeOut" },
    },
  };

  const rightVariant = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, delay: 0.7, ease: "easeOut" },
    },
  };

  // Layout: if reverse, image left, text right
  const leftGrid = hasLeftContent ? (
    <Grid
      item
      xs={12}
      md={6}
      lg={6}
      sx={{
        width: { md: "50%", lg: "50%", xl: "50%", xxl: "50%" },
        color: "#fff",
      }}
    >
      <motion.div
        variants={leftVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            textAlign: "left",
            borderRadius: 0,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, sm: 4, md: 5 },
            position: "relative",
            ...leftContentSx,
          }}
        >
          {/* Heading */}
          {heading && (
            <motion.div
              variants={headingVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "1.3rem", sm: "1.3rem", md: "2.0rem" },
                  fontFamily: '"Poppins",serif',
                  display: "inline-block",
                  color: "#fff",
                  letterSpacing: "-0.02em",
                  ...headingSx,
                }}
              >
                {heading}
              </Typography>
            </motion.div>
          )}
          {/* Description */}
          {description && (
            <motion.div
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  color: "#fff",
                  mt: 1,
                  fontWeight: "400",
                  fontSize: {
                    xs: "0.95rem",
                    sm: "0.95rem",
                    md: "1.05rem",
                  },
                  ...descriptionSx,
                }}
              >
                {description}
              </Typography>
            </motion.div>
          )}
        </Box>
      </motion.div>
    </Grid>
  ) : null;

  // Only render rightGrid if image is provided
  const rightGrid = hasRightContent ? (
    <Grid
      item
      xs={12}
      md={6}
      sx={{
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        variants={rightVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{
            maxWidth: "500px",
            height: "auto",
            borderRadius: 2,
            display: "block",
            mx: "auto",
            width: {
              xs: "300px",
              sm: "350px",
              md: "400px",
              lg: "500px",
            },
            mb: { xs: 5, sm: 5, md: 3, lg: 1 },
            ...rightImageSx,
          }}
        />
      </motion.div>
    </Grid>
  ) : null;

  // Always use 100vh for the hero section
  // If bannerImage, use it as background, then overlay a linear gradient
  // If only gradientBg, use it as background
  const backgroundStyles = {
    width: "100vw",
    maxWidth: "100vw",
    minHeight: "100vh",
    height: "100vh",
    py: 0,
    p: { xs: 2, md: 0 },
    mt: { md: "0rem", sm: "0rem", xs: "0rem" },
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    ...containerSx,
  };

  let backgroundImageStyle = {};
  if (bannerImage) {
    backgroundImageStyle.backgroundImage = `url(${bannerImage})`;
    backgroundImageStyle.backgroundSize = "cover";
    backgroundImageStyle.backgroundPosition = "center";
    backgroundImageStyle.backgroundRepeat = "no-repeat";
    backgroundImageStyle.position = "relative";
    backgroundImageStyle.width = "100%";
    backgroundImageStyle.height = "100%";
  } else if (gradientBg) {
    backgroundImageStyle.background =
      "linear-gradient(268.53deg, #1363DF 8.18%, #000000 124.21%)";
    backgroundImageStyle.position = "relative";
    backgroundImageStyle.width = "100%";
    backgroundImageStyle.height = "100%";
  }

  return (
    <Box sx={{ ...backgroundStyles, ...backgroundImageStyle }}>
      {/* Linear gradient overlay (on top of banner image or as main background if no image) */}
      {(bannerImage || gradientBg) && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            background: gradientBg
              ? gradientBg
              : "linear-gradient(268.53deg, #1363DF 28.18%, #000000 124.21%)",
            opacity: gradientOpacity ?? 0.9,
          }}
        />
      )}
      {/* Always render Header at the top */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 0,
          width: "100%",
        }}
      >
        <Header />
      </Box>
      {(hasLeftContent || hasRightContent) && (
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            px: 0,
            position: "relative",
            height: "100vh",
            display: "flex",
            alignItems: "center",
          }}
          className="max-w-screen-xl"
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{
              height: "100vh",
              color: "#fff",
            }}
          >
            {reverse ? (
              <>
                {rightGrid}
                {leftGrid}
              </>
            ) : (
              <>
                {leftGrid}
                {rightGrid}
              </>
            )}
          </Grid>
        </Container>
      )}
    </Box>
  );
};

export default HeroSection;
